import React, { useState } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { RadioGroup, Radio } from 'components/RadioGroup'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Text } from 'blaze-ui/Text/Text'
import { StackModalProps } from 'almanac-ui/Modal'
import client from 'api/client'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useDeleteExternalIntegration } from 'hooks/queries/externalIntegrations/useDeleteExternalIntegration'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useUpdateBrandPlanSelectedPlatform } from './hooks/useUpdateBrandPlanSelectedPlatform'
import Styles from './SwitchToDifferentPlatformModal.module.scss'

interface SwitchToDifferentPlatformModalProps extends StackModalProps {
  integration?: ExternalIntegration
  tactic: 'seo' | 'email'
}

// Platform options based on the image
const BLOG_PLATFORMS = [
  'Medium',
  'Wix',
  'Squarespace',
  'Shopify',
  'Blogger',
  'Substack',
  'LinkedIn Articles',
  'Weebly',
  'Tumblr',
  'Beehiiv',
  'Ghost',
  'HubSpot CMS',
]

const EMAIL_PLATFORMS = [
  'ActiveCampaign',
  'Allegrow',
  'Brevo',
  'Campaign Monitor',
  'HubSpot',
  'Mailjet',
  'Mailgun',
  'Postmark',
  'SendGrid',
  'Sidemail',
  'ZeroBounce',
  'Zoho Campaigns',
]

export const SwitchToDifferentPlatformModal = ({ integration, close, tactic }: SwitchToDifferentPlatformModalProps) => {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('')
  const [otherPlatform, setOtherPlatform] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const updateBrandPlanSelectedPlatform = useUpdateBrandPlanSelectedPlatform()
  const { mutateAsync: deleteIntegration } = useDeleteExternalIntegration(integration?.id || -1, {
    onSuccess: () => {
      queryClient.invalidateQueries(['externalIntegrations', workspaceId])
    },
  })

  const platforms = tactic === 'seo' ? BLOG_PLATFORMS : EMAIL_PLATFORMS

  const handleConfirm = async () => {
    // If a platform is selected from the list, use it; otherwise use the "other" input
    const platform = selectedPlatform && selectedPlatform !== 'other' ? selectedPlatform : otherPlatform.trim()

    if (!platform) {
      showToastMessage('Please select a platform or enter a platform name', 'error')
      return
    }

    setIsLoading(true)
    try {
      // Track the event
      await client.trackEvent('Event::SwitchToDifferentPlatform', {
        platform,
        tactic,
      })

      // Remove the existing connection if there is one
      if (integration) {
        await deleteIntegration()
      }

      await updateBrandPlanSelectedPlatform(tactic, undefined)

      showToastMessage('Platform preference saved', 'success')
      close()
    } catch (error) {
      showToastMessage('Failed to save platform preference', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal.Root onClose={close}>
      <Modal.Header title="Tell us which platform you use" />
      <Modal.Content>
        <div className={Styles.content}>
          <RadioGroup
            value={selectedPlatform}
            onChange={setSelectedPlatform}
            aria-label="Select platform"
            className={Styles.radioGroup}
          >
            <div className={Styles.platformsGrid}>
              {platforms.map((platform) => (
                <Radio key={platform} value={platform} aria-label={platform}>
                  {platform}
                </Radio>
              ))}
            </div>
            <div className={Styles.otherPlatformContainer}>
              <Radio value="other" aria-label="Other platform">
                <Text variant="label" color="var(--dark-80)">
                  Other platform
                </Text>
              </Radio>
              <Input
                name="otherPlatform"
                value={otherPlatform}
                onChange={(e) => {
                  setOtherPlatform(e.target.value)
                  if (e.target.value.trim() && selectedPlatform !== 'other') {
                    setSelectedPlatform('other')
                  }
                }}
                placeholder="Enter the name of the platform you use"
                size="lg"
                className={Styles.otherPlatformInput}
              />
            </div>
          </RadioGroup>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={close}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            size="lg"
            variant="primary"
            onPress={handleConfirm}
            isDisabled={!selectedPlatform || (selectedPlatform === 'other' && !otherPlatform.trim()) || isLoading}
          >
            Confirm
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
